/**
 * CheckOutModal（退社確認ダイアログ）
 * 詳細設計書 §4.3 に対応。
 *
 * Props:
 *   isOpen       {boolean}
 *   seatNumber   {string}
 *   checkInTime  {string|null} ISO 文字列
 *   onConfirm    {function} 退社確定コールバック（checkOutData を渡す）
 *   onCancel     {function}
 */
import { useState, useEffect, useRef } from 'react';
import { confirmCheckOut } from '../api/lifeApi';

function formatTime(isoString) {
  if (!isoString) return '--:--';
  return new Date(isoString).toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CheckOutModal({ isOpen, seatNumber, checkInTime, onConfirm, onCancel }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dialogRef = useRef(null);

  // フォーカストラップ用：モーダルが開いたら最初の要素にフォーカス
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll('button');
      if (focusable.length) focusable[0].focus();
    }
  }, [isOpen]);

  // Esc キーで閉じる
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await confirmCheckOut();
      onConfirm(res?.data ?? null);
    } catch (err) {
      setError(err.message || '退社処理に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="life-modal-overlay"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="life-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-modal-title"
        ref={dialogRef}
      >
        <h2 id="checkout-modal-title" className="life-modal__title">退社確認</h2>
        <p className="life-modal__desc">
          退社処理を実行すると、本日中は再度出社登録ができなくなります。
        </p>

        {/* 現在の座席・出社時刻 */}
        <div className="life-modal__info">
          <div className="life-record-row">
            <span className="life-record-row__label">現在の座席</span>
            <span className="life-record-row__value">{seatNumber}</span>
          </div>
          <div className="life-record-row">
            <span className="life-record-row__label">出社時刻</span>
            <span className="life-record-row__value">{formatTime(checkInTime)}</span>
          </div>
        </div>

        <p className="life-modal__warning">⚠️ この操作は取り消せません。</p>

        {error && (
          <div className="life-error" role="alert" style={{ marginBottom: 12 }}>
            {error}
          </div>
        )}

        <div className="life-modal__actions">
          <button
            className="life-btn life-btn--outline"
            onClick={onCancel}
            disabled={loading}
          >
            キャンセル
          </button>
          <button
            className="life-btn life-btn--destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="life-spinner life-spinner--dark" aria-hidden="true" />
                処理中...
              </>
            ) : (
              '退社する'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CheckOutModal;
